import * as React from 'react';
import type {
  Validations,
  NormalizedSms,
  SmsState,
  // FetchedSmsResponse,
  SmsApi,
} from 'src/types/sms';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import type {EntityRelationship} from 'src/types/ats-entities';
import {escapeVarNames} from 'src/components/lib/automation-module-registry/utils';
import {getStringWithoutAutomationIdentifiers} from 'src/utils/automation';

import {
  normalizeNodeSchedule,
  parseNodeSchedule,
} from 'src/utils/node-schedule';
import {FormLabelSmall} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  normalizeMessagingNoteWriteback,
  parseMessagingNoteWriteback,
} from 'src/utils/note-writeback';
import {normalizeNodeAlerts, parseNodeAlerts} from 'src/utils/node-alerts';


export const SMS_CONTENT_SIZE = 720;

export const SMS_TRANSMISSION_LEARN_MORE_LINK =
  'https://support.sensehq.co/hc/en-us/articles/31332020693901-SMS-Transmission-and-Best-Practices';

export const smsKeywords = [
  'help',
  'start',
  'stop',
  'end',
  'cancel',
  'unsubscribe',
  'quit',
] as const;

export const getSendFromOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {label: t('Variable', 'Variable'), key: 'variable'},
  {
    label: t('Content_category_phone_number', 'Content category phone number'),
    key: 'content_category_phone_number',
  },
  {
    label: t(
      'Content_subscription_backup_number',
      'Content subscription backup number',
    ),
    key: 'content_subscription_backup_number',
  },
];

export const normalizeSms = (smsState: SmsState): NormalizedSms => {
  const {
    to_number,
    from_number,
    category_id,
    external_source_id,
    body,
    sender_category,
  } = smsState;
  const nodeSchedule = normalizeNodeSchedule(smsState);
  const noteWriteback = normalizeMessagingNoteWriteback(smsState);
  const nodeAlerts = normalizeNodeAlerts(smsState.alerts ?? []);
  return {
    task: {
      params: {
        to_number,
        from_number,
        external_source_id,
        category_id,
        body,
        sender_category,
      },
    },
    ...nodeSchedule,
    ...noteWriteback,
    ...nodeAlerts,
  };
};

export const getSenderListWithItsEntityVariables = (
  anchorEntityType: string,
  relatedInternalEntities: EntityRelationship[],
): Array<{
  label: string;
  value: string;
  variableValue: string;
}> => {
  const senderVariables = anchorEntityType
    ? relatedInternalEntities.map((e) => ({
        label: e.display_name,
        value: `<${e.join_attribute}>`,
        variableValue: `{{$.entity_group_fetch__${anchorEntityType}.response.relatedEntities.${escapeVarNames(
          e.name,
        )}.entity.externalSourceId}}`,
      }))
    : [];

  return senderVariables;
};

type SenderOption = {
  label: string;
  value: string;
  variableValue: string;
};

export const getSenderNameFromVariable = (
  senderVariable: string,
  senderOptions: Array<SenderOption>,
): string => {
  const currentSender = senderOptions.find(
    (opt) => opt.variableValue === senderVariable,
  );
  return currentSender?.label ?? '';
};

export const getErrors = (validationsObject: Validations): Array<string> => {
  const errors: Array<string> = [];
  const {
    senderNumberEmpty,
    sendToNumberEmpty,
    sameFromToNumber,
    invalidToNumber,
    nwbSentEntityNotSelected,
    nwbSentNoteActionNotSelected,
    nwbReplyEntityNotSelected,
    nwbReplyNoteActionNotSelected,
  } = validationsObject;
  if (senderNumberEmpty || sendToNumberEmpty) {
    errors.push('Please fill the required fields.');
  }
  if (sameFromToNumber) {
    errors.push('Sender Number cannot be same as Receiver Number.');
  }
  if (invalidToNumber) {
    errors.push('Send To Number is Invalid.');
  }
  if (nwbSentEntityNotSelected) {
    errors.push('Please select an entity for sent note writeback.');
  }
  if (nwbSentNoteActionNotSelected) {
    errors.push('Please select a sent note action.');
  }
  if (nwbReplyEntityNotSelected) {
    errors.push('Please select an entity for reply note writeback.');
  }
  if (nwbReplyNoteActionNotSelected) {
    errors.push('Please select a reply note action.');
  }
  return errors;
};

export const getContentErrors = (validationsObject: {
  contentBodyEmpty: boolean;
  contentLengthExceeded: boolean;
}): Array<string> => {
  const errors: Array<string> = [];
  if (validationsObject.contentBodyEmpty) {
    errors.push('Content body is required.');
  }
  if (validationsObject.contentLengthExceeded) {
    errors.push('SMS content is too long.');
  }
  return errors;
};

export const parseSms = (fetchedNodeResponse: SmsApi): SmsState => {
  const smsSendTask = fetchedNodeResponse.task;
  const taskDefinition = smsSendTask.params;
  if (!taskDefinition) {
    throw new Error('Sms task definition not found');
  }
  const nodeSchedule = parseNodeSchedule(fetchedNodeResponse);
  const noteWriteback = parseMessagingNoteWriteback(fetchedNodeResponse);
  const nodeAlerts = parseNodeAlerts(fetchedNodeResponse);
  const toNumber = String(taskDefinition.to_number);
  const surveysAttached = smsSendTask.surveys_attached ?? [];
  const meetingsAttached = smsSendTask.meetings_attached ?? [];

  return {
    body: taskDefinition.body,
    // @ts-expect-error - TS2345 - Argument of type 'number | null | undefined' is not assignable to parameter of type 'string'.
    category_id: parseInt(taskDefinition.category_id, 10),
    external_source_id: taskDefinition.external_source_id
      ? taskDefinition.external_source_id
      : null,
    from_number: taskDefinition.from_number ?? null,
    to_number:
      toNumber.startsWith('{{') && toNumber.endsWith('}}')
        ? toNumber
        : `{{${toNumber || ''}}}`,
    sender_category: taskDefinition.sender_category,
    surveysAttached,
    meetingsAttached,
    alerts: nodeAlerts.alerts ?? [],
    ...nodeSchedule,
    ...noteWriteback,
  };
};

export const isGreaterThanSmsContentSize = (value: string): boolean => {
  return getStringWithoutAutomationIdentifiers(value).length > SMS_CONTENT_SIZE;
};

export const getCombinedSenderOptions = ({
  anchorEntityType,
  triggerEntityType,
  relatedInternalEntities,
  triggerInternalEntities,
  standardEntityType,
}: {
  anchorEntityType: string;
  triggerEntityType: string;
  relatedInternalEntities: EntityRelationship[];
  triggerInternalEntities: EntityRelationship[];
  standardEntityType: string;
}): Array<SenderOption> => {
  const anchorSenderOptions = getSenderListWithItsEntityVariables(
    anchorEntityType || '',
    relatedInternalEntities,
  );

  const triggerSenderOptions =
    triggerEntityType !== anchorEntityType && standardEntityType === 'job'
      ? getSenderListWithItsEntityVariables(
          triggerEntityType || '',
          triggerInternalEntities,
        )
      : [];

  return [...anchorSenderOptions, ...triggerSenderOptions];
};

export const getSenderOptionsForDropdown = ({
  anchorEntityType,
  triggerEntityType,
  relatedInternalEntities,
  triggerInternalEntities,
  standardEntityType,
  t,
}: {
  anchorEntityType: string;
  triggerEntityType: string;
  relatedInternalEntities: EntityRelationship[];
  triggerInternalEntities: EntityRelationship[];
  standardEntityType: string;
  t: (arg1: string, arg2: string) => string;
}): Array<{
  groupTitle: React.ReactNode;
  options?: Array<MenuOption>;
  showLineDivider?: boolean;
}> => {
  const anchorSenderOptions = getSenderListWithItsEntityVariables(
    anchorEntityType || '',
    relatedInternalEntities,
  );

  const triggerSenderOptions =
    triggerEntityType !== anchorEntityType && standardEntityType === 'job'
      ? getSenderListWithItsEntityVariables(
          triggerEntityType || '',
          triggerInternalEntities,
        )
      : [];

  return [
    {
      groupTitle: (
        <FormLabelSmall color="tertiary">{`${t(
          'Candidate_variables',
          'Candidate variables',
        )} (${anchorSenderOptions.length})`}</FormLabelSmall>
      ),
      options: anchorSenderOptions.map(({label, variableValue}) => ({
        key: variableValue,
        label,
      })),
      showLineDivider: true,
    },
    {
      groupTitle: (
        <FormLabelSmall color="tertiary">{`${t(
          'Joborder_variables',
          'Joborder variables',
        )} (${triggerSenderOptions.length})`}</FormLabelSmall>
      ),
      options: triggerSenderOptions.map(({label, variableValue}) => ({
        key: variableValue,
        label,
      })),
      showLineDivider: true,
    },
  ].filter((group) => (group.options?.length ?? 0) > 0);
};
